import { isValidElement, useMemo } from "react";
import { useIntl } from "react-intl";
import { useFilterContext } from "../contexts/FilterProvider";
import { basicSort } from "../utils/basicSort";
import { FILTER_SELECTION_TYPES } from "../constants/filterSelectionTypes";
import { capitalizeFirstLetter } from "app/utils/string/capitalize";
import { OPTION_MODE } from "../constants/optionMode";
import { FILTER_DISPLAY } from "../constants/filterDisplay";

const MIN_SEARCHABLE_OPTIONS = 20;

/**
 * Valeur non vide (utile pour nettoyer des inputs venant d'URL / backend).
 */
const isNonEmpty = value => value !== undefined && value !== null && String(value).trim() !== "";

/**
 * Détecte si un élément est un JSX valide (pour labelPrefix par ex).
 */
const isJSX = element => isValidElement(element);

/**
 * Convertit une valeur en nombre si possible, sinon null.
 */
const toNumber = value => {
	if (value === undefined || value === null) {
		return null;
	}
	const s = String(value).trim();
	if (s === "") {
		return null;
	}
	const n = Number(s);
	return Number.isFinite(n) ? n : null;
};

/**
 * Parse une valeur de range encodée sous forme "between_<min>_<max>".
 * Retourne {min, max} ou null.
 */
const parseBetweenRange = rangeValue => {
	const s = String(rangeValue || "");
	if (!s.startsWith("between_")) {
		return null;
	}

	const parts = s.split("_");
	if (parts.length < 3) {
		return null;
	}

	const min = Number(parts[1]);
	const max = Number(parts[2]);

	if (!Number.isFinite(min) || !Number.isFinite(max)) {
		return null;
	}
	return { min, max };
};

/**
 * Sérialise un range {min, max} vers "between_<min>_<max>".
 */
const toBetweenValue = ({ min, max }) => `between_${min}_${max}`;

/**
 * Normalise une sélection RANGE (qui peut venir sous plusieurs formes) vers un unique string "between_min_max".
 * - Si on reçoit déjà "between_x_y" => on renvoie tel quel (normalisé)
 * - Si slider à 2 curseurs => [a,b] => between_min(a,b)_max(a,b)
 * - Si slider à 1 curseur => [x] => between_domainMin_x
 */
const normalizeRangeSelectionToBetween = ({ selection, domain, thumbCount = 2 }) => {
	if (!domain || !Number.isFinite(domain.min) || !Number.isFinite(domain.max)) {
		return null;
	}

	const arr = Array.isArray(selection) ? selection : [selection];
	const cleaned = arr.filter(isNonEmpty);

	if (cleaned.length === 0) {
		return null;
	}

	if (typeof cleaned[0] === "string") {
		const parsed = parseBetweenRange(cleaned[0]);
		if (parsed) {
			return toBetweenValue(parsed);
		}
	}

	if (thumbCount === 2 && cleaned.length >= 2) {
		const a = toNumber(cleaned[0]);
		const b = toNumber(cleaned[1]);
		if (a === null || b === null) {
			return null;
		}

		const min = Math.min(a, b);
		const max = Math.max(a, b);
		return toBetweenValue({ min, max });
	}

	const x = toNumber(cleaned[0]);
	if (x === null) {
		return null;
	}

	return toBetweenValue({ min: domain.min, max: x });
};

/**
 * Libellé pour un filtre RANGE actif (affichage type: "Prix 100 - 200").
 * Supporte :
 * - formatRangeValue(v) pour formatter les bornes
 * - formatRangeActiveLabel({min, max}) pour surcharger le libellé final
 */
export const formatActiveRangeLabel = (filter, rawValue) => {
	if (!filter) {
		return String(rawValue ?? "");
	}
	if (filter.selectionMode !== FILTER_SELECTION_TYPES.RANGE) {
		return String(rawValue ?? "");
	}

	const parsed = parseBetweenRange(rawValue);
	if (!parsed) {
		return String(rawValue ?? "");
	}

	const formatValue =
		typeof filter.formatRangeValue === "function" ? filter.formatRangeValue : v => String(v);

	const formattedMin = formatValue(parsed.min);
	const formattedMax = formatValue(parsed.max);

	if (typeof filter.formatRangeActiveLabel === "function") {
		return filter.formatRangeActiveLabel({ min: formattedMin, max: formattedMax });
	}

	return `${filter?.title} ${formattedMin} - ${formattedMax}`;
};

/**
 * Aplati la config de facets => liste de filtres.
 */
const flattenFilters = facetConfigs =>
	(Array.isArray(facetConfigs) ? facetConfigs : [])
		.flatMap(f => (Array.isArray(f?.filters) ? f.filters : []))
		.filter(Boolean);

/**
 * Extrait les options (value/label) d'un produit via valueGetter/labelGetter.
 * Retourne une liste d'options uniques par produit (mais pas globalement).
 */
const getOptionsFromProduct = ({ product, valueGetter, labelGetter }) => {
	try {
		const rawValue = valueGetter(product);
		const rawLabel = labelGetter ? labelGetter(product, rawValue) : rawValue;

		if (rawValue === undefined || rawValue === null) {
			return [];
		}

		const values = Array.isArray(rawValue) ? rawValue : [rawValue];
		const labels = Array.isArray(rawLabel) ? rawLabel : [rawLabel];

		if (values.length !== labels.length) {
			return [];
		}

		return values.filter(isNonEmpty).map((v, idx) => ({
			value: String(v),
			label: isNonEmpty(labels[idx]) ? String(labels[idx]) : String(v),
		}));
	} catch {
		return [];
	}
};

/**
 * Calcule le domaine (min/max) d'un champ numérique pour un filtre RANGE.
 */
const computeRangeDomain = ({ products = [], valueGetter }) => {
	let min = Infinity;
	let max = -Infinity;

	for (const p of products) {
		const raw = valueGetter(p);
		const v = toNumber(Array.isArray(raw) ? raw[0] : raw);

		if (v === null) {
			continue;
		}
		if (v < min) {
			min = v;
		}
		if (v > max) {
			max = v;
		}
	}

	if (!Number.isFinite(min) || !Number.isFinite(max)) {
		return null;
	}
	return { min, max };
};

const defaultSort = (a, b) => basicSort(a.label, b.label);

/**
 * Construit la liste d'options "auto" en scannant les produits
 * et en dédupliquant par valeur (Map).
 */
const buildAutoOptions = ({ products = [], valueGetter, labelGetter }) => {
	const map = new Map();

	for (const product of products) {
		const opts = getOptionsFromProduct({ product, valueGetter, labelGetter });

		for (const opt of opts) {
			const v = String(opt?.value ?? "").trim();
			if (!v) {
				continue;
			}

			if (!map.has(v)) {
				map.set(v, { value: v, label: String(opt?.label ?? v) });
			}
		}
	}

	return Array.from(map.values());
};

/**
 * Fusionne les options auto et manuelles selon le mode :
 * - MANUAL_ONLY => uniquement les manuelles
 * - sinon => auto + manuelles (les manuelles prennent le dessus si même value)
 *
 * Note : dans l'implémentation actuelle, on concatène auto puis manuelles,
 * en retirant des auto les valeurs déjà présentes en manuel.
 */
const mergeOptions = ({ manualOptions, autoOptions, optionMode }) => {
	if (optionMode === OPTION_MODE.MANUAL_ONLY) {
		return manualOptions ?? [];
	}

	if (manualOptions?.length > 0) {
		const seen = new Set(manualOptions.map(o => String(o?.value ?? "").trim()).filter(Boolean));
		const autoWithoutDup = autoOptions.filter(o => !seen.has(String(o?.value ?? "").trim()));
		return [...autoWithoutDup, ...manualOptions];
	}

	return autoOptions;
};

/**
 * Récupère les valeurs sélectionnées pour un champ dans activeFilters
 * sous forme de tableau de strings nettoyées.
 */
const getSelectedValues = (activeFilters, field) => {
	const raw = activeFilters?.[field];
	const arr = Array.isArray(raw) ? raw : [raw];

	return arr
		.filter(isNonEmpty)
		.map(v => String(v).trim())
		.filter(Boolean);
};

/**
 * Construit des index pour accélérer :
 * - la recherche par valeur exacte (Set d'indexes produits par value)
 * - le calcul des counts exacts
 * - les filtres RANGE (valeurs numériques par produit)
 *
 * Structures :
 * - valueToProductSetByField[field].get(value) => Set(productIndex)
 * - valueByProductByField[field][idx] => Array(values)
 * - numericByProductByField[field][idx] => number|null
 */
const buildIndexes = ({ products = [], filters }) => {
	const valueByProductByField = Object.create(null);
	const valueToProductSetByField = Object.create(null);
	const numericByProductByField = Object.create(null);

	for (const cfg of filters) {
		const { field, valueGetter, labelGetter, selectionMode } = cfg;

		if (selectionMode === FILTER_SELECTION_TYPES.RANGE) {
			const nums = new Array(products.length);

			for (const [i, product] of products.entries()) {
				const raw = valueGetter(product);
				const v = toNumber(Array.isArray(raw) ? raw[0] : raw);
				nums[i] = v;
			}

			numericByProductByField[field] = nums;
			continue;
		}

		const valuesArr = new Array(products.length);
		const mapValueToSet = new Map();

		for (const [i, product] of products.entries()) {
			const opts = getOptionsFromProduct({ product, valueGetter, labelGetter });

			const seen = new Set();
			const uniq = [];

			for (const opt of opts) {
				const v = String(opt?.value ?? "").trim();
				if (!v) {
					continue;
				}
				if (seen.has(v)) {
					continue;
				}

				seen.add(v);
				uniq.push(v);

				let set = mapValueToSet.get(v);
				if (!set) {
					set = new Set();
					mapValueToSet.set(v, set);
				}
				set.add(i);
			}

			valuesArr[i] = uniq;
		}

		valueByProductByField[field] = valuesArr;
		valueToProductSetByField[field] = mapValueToSet;
	}

	return { valueByProductByField, valueToProductSetByField, numericByProductByField };
};

/**
 * Intersecte base avec other, en modifiant base directement.
 */
const intersectInPlace = (base, other) => {
	for (const v of base) {
		if (!other.has(v)) {
			base.delete(v);
		}
	}
	return base;
};

/**
 * Union de plusieurs Sets (ignore les sets null/undefined).
 */
const unionSets = sets => {
	const out = new Set();
	for (const s of sets) {
		if (!s) {
			continue;
		}
		for (const v of s) {
			out.add(v);
		}
	}
	return out;
};

/**
 * Calcule l'ensemble des produits qui matchent les filtres actifs.
 *
 * Principe :
 * - On construit progressivement un Set "current" des indexes produits qui passent.
 * - Pour RANGE : on filtre via le tableau numérique indexé.
 * - Pour EXACT :
 *   - valeurs "auto" => on passe par les index (rapide) si dispo
 *   - valeurs "manuelles" => uniquement si le champ est déclaré "manuel+customMatcher",
 *     alors on applique customMatcher sur ces valeurs-là
 *
 * skipField : permet de calculer "matched sans ce champ" (utile pour les counts).
 */
const buildMatchedSet = ({
	products = [],
	filters,
	activeFilters,
	indexes,
	skipField,
	manualValueSetByField,
}) => {
	const n = products.length;
	let current = null;

	for (const cfg of filters) {
		const { field, selectionMode, customMatcher, valueGetter } = cfg;

		if (field === skipField) {
			continue;
		}

		if (selectionMode === FILTER_SELECTION_TYPES.RANGE) {
			const rangeValue = activeFilters?.[field]?.[0];
			const parsed = parseBetweenRange(rangeValue);
			if (!parsed) {
				continue;
			}

			const nums = indexes?.numericByProductByField?.[field];
			if (!nums) {
				continue;
			}

			const next = new Set();

			if (current) {
				for (const idx of current) {
					const v = nums[idx];
					if (v !== null && v !== undefined && v >= parsed.min && v <= parsed.max) {
						next.add(idx);
					}
				}
			} else {
				for (const [i, v] of nums.entries()) {
					if (v !== null && v !== undefined && v >= parsed.min && v <= parsed.max) {
						next.add(i);
					}
				}
			}

			current = next;
			if (current.size === 0) {
				return current;
			}
			continue;
		}

		const selected = getSelectedValues(activeFilters, field);
		if (selected.length === 0) {
			continue;
		}

		const manualSet = manualValueSetByField?.get(field) ?? null;

		// Si ce champ a des options manuelles gérées par customMatcher,
		// on sépare la sélection en "manuel" vs "auto" (exact/index).
		let manualSelected = [];
		let autoSelected = selected;

		if (manualSet && manualSet.size > 0) {
			manualSelected = [];
			autoSelected = [];

			for (const v of selected) {
				if (manualSet.has(v)) {
					manualSelected.push(v);
				} else {
					autoSelected.push(v);
				}
			}
		}

		let candidate = null;

		// 1) Chemin "auto" : match exact via index (sinon fallback scan)
		if (autoSelected.length > 0) {
			const indexMap = indexes?.valueToProductSetByField?.[field];

			if (indexMap) {
				const sets = [];
				for (const v of autoSelected) {
					sets.push(indexMap.get(v));
				}
				candidate = unionSets(sets);
			} else {
				const baseToScan = current
					? current
					: new Set(Array.from({ length: n }, (_, i) => i));
				const next = new Set();

				for (const idx of baseToScan) {
					const p = products[idx];
					const raw = valueGetter(p);
					const vals = (Array.isArray(raw) ? raw : [raw])
						.filter(isNonEmpty)
						.map(v => String(v).trim());

					let ok = false;
					for (const v of autoSelected) {
						if (vals.includes(v)) {
							ok = true;
							break;
						}
					}

					if (ok) {
						next.add(idx);
					}
				}

				candidate = next;
			}
		}

		// 2) Chemin "manuel" : application du customMatcher uniquement sur les valeurs manuelles
		if (manualSelected.length > 0 && typeof customMatcher === "function") {
			const baseToScan = current ? current : new Set(Array.from({ length: n }, (_, i) => i));
			const matchedManual = new Set();

			for (const idx of baseToScan) {
				const p = products[idx];
				if (customMatcher(p, manualSelected, activeFilters)) {
					matchedManual.add(idx);
				}
			}

			if (candidate) {
				for (const idx of matchedManual) {
					candidate.add(idx);
				}
			} else {
				candidate = matchedManual;
			}
		}

		if (!candidate) {
			return new Set();
		}

		if (current) {
			intersectInPlace(current, candidate);
		} else {
			current = candidate;
		}

		if (current.size === 0) {
			return current;
		}
	}

	// Aucun filtre actif => tout passe
	if (!current) {
		return new Set(Array.from({ length: products.length }, (_, i) => i));
	}

	return current;
};

/**
 * Compte les occurrences des valeurs exactes (auto) sur un ensemble de produits donné,
 * en s'appuyant sur l'index valueByProductByField.
 */
const computeCountsExactFast = ({ field, matchedWithoutFieldSet, indexes }) => {
	const counts = new Map();
	const valuesArr = indexes?.valueByProductByField?.[field];
	if (!valuesArr) {
		return counts;
	}

	for (const idx of matchedWithoutFieldSet) {
		const vals = valuesArr[idx];
		if (!vals || vals.length === 0) {
			continue;
		}

		for (const v of vals) {
			counts.set(v, (counts.get(v) || 0) + 1);
		}
	}

	return counts;
};

/**
 * Calcule les counts pour les options manuelles en utilisant customMatcher.
 * On évalue chaque valeur manuelle sur chaque produit candidat.
 */
const computeCountsManualWithCustomMatcher = ({
	products,
	matchedWithoutFieldSet,
	manualOptions,
	customMatcher,
	activeFilters,
}) => {
	const counts = new Map();

	if (!Array.isArray(manualOptions) || manualOptions.length === 0) {
		return counts;
	}
	if (typeof customMatcher !== "function") {
		return counts;
	}

	const manualValues = [];
	for (const opt of manualOptions) {
		const v = String(opt?.value ?? "").trim();
		if (v) {
			manualValues.push(v);
			counts.set(v, 0);
		}
	}

	if (manualValues.length === 0) {
		return counts;
	}

	for (const idx of matchedWithoutFieldSet) {
		const p = products[idx];

		for (const v of manualValues) {
			if (customMatcher(p, [v], activeFilters)) {
				counts.set(v, (counts.get(v) || 0) + 1);
			}
		}
	}

	return counts;
};

/**
 * Formate et enrichit les options :
 * - traduction via labelKey (react-intl) si présent
 * - capitalisation
 * - formatLabel / buildOptionPrefix éventuels
 * - ajout du count
 */
const formatAndEnrichOptions = ({
	options,
	formatMessage,
	formatLabel,
	buildOptionPrefix,
	countsMap,
	showCount,
}) => {
	if (!options || options.length === 0) {
		return [];
	}

	const out = [];

	for (const option of options) {
		const { value, label, labelKey, labelKeyOptions, formatLabelOptions, labelPrefix } = option;

		const rawValue = String(value ?? "").trim();
		if (!rawValue) {
			continue;
		}

		const labelToFormat = labelKey ? formatMessage({ id: labelKey }, labelKeyOptions) : label;
		const capitalized = capitalizeFirstLetter(labelToFormat);

		const finalLabel =
			typeof formatLabel === "function"
				? formatLabel({ label: capitalized, formatLabelOptions })
				: capitalized;

		if (!finalLabel) {
			continue;
		}

		const prefix =
			typeof buildOptionPrefix === "function"
				? buildOptionPrefix({ value: rawValue, label: finalLabel, formatLabelOptions })
				: labelPrefix;

		const count = countsMap ? (countsMap.get(rawValue) ?? 0) : 0;

		out.push({
			value: rawValue,
			label: finalLabel,
			labelPrefix: isJSX(prefix) ? prefix : undefined,
			count,
			showCount,
		});
	}

	return out;
};

export const useFacets = externalFilters => {
	const { formatMessage } = useIntl();

	const {
		facetConfigs,
		filters: filtersFromUrl,
		products = [],
		facets: facetsFromBackend,
	} = useFilterContext();

	const activeFilters = externalFilters || filtersFromUrl;

	// Liste plate des filtres de tous les facets
	const flatFilters = useMemo(() => flattenFilters(facetConfigs), [facetConfigs]);

	// Normalisation des filtres RANGE pour garantir un format unique "between_min_max"
	const normalizedActiveFilters = useMemo(() => {
		if (!Array.isArray(products) || !flatFilters.length) {
			return activeFilters;
		}

		const next = { ...(activeFilters || {}) };

		for (const cfg of flatFilters) {
			if (cfg.selectionMode !== FILTER_SELECTION_TYPES.RANGE) {
				continue;
			}

			const domain = computeRangeDomain({ products, valueGetter: cfg.valueGetter });
			if (!domain) {
				continue;
			}

			const thumbCount = cfg.rangeThumbCount ?? 2;

			const normalized = normalizeRangeSelectionToBetween({
				selection: next[cfg.field],
				domain,
				thumbCount,
			});

			if (normalized) {
				next[cfg.field] = [normalized];
			}
		}

		return next;
	}, [activeFilters, flatFilters, products]);

	// Construction des index pour accélérer matching et counts
	const indexes = useMemo(() => {
		if (!Array.isArray(products) || !flatFilters.length) {
			return null;
		}
		return buildIndexes({ products, filters: flatFilters });
	}, [products, flatFilters]);

	/**
	 * Champs pour lesquels on considère qu'il existe des "valeurs manuelles"
	 * gérées par customMatcher (buildOptions + customMatcher).
	 * Cela permet de séparer la sélection en "manuel" vs "auto/index".
	 */
	const manualValueSetByField = useMemo(() => {
		const map = new Map();

		if (!Array.isArray(products) || !flatFilters.length) {
			return map;
		}

		for (const cfg of flatFilters) {
			const { field, buildOptions, customMatcher } = cfg;

			if (typeof buildOptions !== "function") {
				continue;
			}
			if (typeof customMatcher !== "function") {
				continue;
			}

			const opts = buildOptions(products) || [];
			const set = new Set();

			for (const opt of opts) {
				const v = String(opt?.value ?? "").trim();
				if (v) {
					set.add(v);
				}
			}

			if (set.size > 0) {
				map.set(field, set);
			}
		}

		return map;
	}, [products, flatFilters]);

	// Produits filtrés selon les filtres actifs (UI / résultats)
	const filteredProducts = useMemo(() => {
		if (!Array.isArray(products)) {
			return [];
		}
		if (!indexes || !flatFilters.length) {
			return products;
		}

		const matchedSet = buildMatchedSet({
			products,
			filters: flatFilters,
			activeFilters: normalizedActiveFilters,
			indexes,
			skipField: null,
			manualValueSetByField,
		});

		const out = [];
		for (const idx of matchedSet) {
			out.push(products[idx]);
		}
		return out;
	}, [products, indexes, flatFilters, normalizedActiveFilters, manualValueSetByField]);

	/**
	 * Construction des facets (options + counts).
	 * Si le backend fournit déjà facetsFromBackend, on les utilise telles quelles.
	 * Sinon, on les reconstruit côté front en s'appuyant sur products + config.
	 */
	const facets = useMemo(() => {
		if (facetsFromBackend) {
			return facetsFromBackend;
		}

		if (!Array.isArray(facetConfigs) || !Array.isArray(products)) {
			return [];
		}
		if (!indexes || !flatFilters.length) {
			return [];
		}

		const builtFacets = [];

		const matchedWithoutFieldCache = new Map();
		const countsCache = new Map();

		// Renvoie l'ensemble des produits qui matchent tous les filtres SAUF "field"
		const getMatchedWithoutField = field => {
			if (matchedWithoutFieldCache.has(field)) {
				return matchedWithoutFieldCache.get(field);
			}

			const set = buildMatchedSet({
				products,
				filters: flatFilters,
				activeFilters: normalizedActiveFilters,
				indexes,
				skipField: field,
				manualValueSetByField,
			});

			matchedWithoutFieldCache.set(field, set);
			return set;
		};

		// Calcule (et met en cache) la Map value => count pour un filtre donné
		const getCountsForFilter = cfg => {
			const field = cfg.field;

			if (countsCache.has(field)) {
				return countsCache.get(field);
			}

			const matchedWithout = getMatchedWithoutField(field);

			// Cas particulier : si on force MANUAL_ONLY, on calcule les counts uniquement via customMatcher
			if (
				cfg.optionMode === OPTION_MODE.MANUAL_ONLY &&
				typeof cfg.customMatcher === "function" &&
				typeof cfg.buildOptions === "function"
			) {
				const manual = cfg.buildOptions(products) || [];
				const manualCounts = computeCountsManualWithCustomMatcher({
					products,
					matchedWithoutFieldSet: matchedWithout,
					manualOptions: manual,
					customMatcher: cfg.customMatcher,
					activeFilters: normalizedActiveFilters,
				});
				countsCache.set(field, manualCounts);
				return manualCounts;
			}

			// Par défaut : counts exacts via index
			const exactCounts = computeCountsExactFast({
				field,
				matchedWithoutFieldSet: matchedWithout,
				indexes,
			});

			// Si ce champ a aussi des options manuelles gérées par customMatcher,
			// on remplace/ajoute les counts pour ces valeurs manuelles uniquement.
			if (typeof cfg.customMatcher === "function" && typeof cfg.buildOptions === "function") {
				const manual = cfg.buildOptions(products) || [];
				const manualCounts = computeCountsManualWithCustomMatcher({
					products,
					matchedWithoutFieldSet: matchedWithout,
					manualOptions: manual,
					customMatcher: cfg.customMatcher,
					activeFilters: normalizedActiveFilters,
				});

				for (const [k, v] of manualCounts.entries()) {
					exactCounts.set(k, v);
				}
			}

			countsCache.set(field, exactCounts);
			return exactCounts;
		};

		for (const facet of facetConfigs) {
			const facetId = facet?.facetId;
			const facetTitle = facet?.facetTitle;
			const facetFilters = Array.isArray(facet?.filters) ? facet.filters : [];

			const filters = [];

			for (const cfg of facetFilters) {
				const {
					field,
					title,
					valueGetter,
					labelGetter,
					formatLabel,
					buildOptions,
					buildOptionPrefix,
					customSort,
					selectionMode = FILTER_SELECTION_TYPES.MULTIPLE,
					allowSearch = false,
					search = {},
					optionMode = OPTION_MODE.MERGE,
					allowSingleOption = false,
					showCount = true,
					formatRangeValue,
					formatRangeActiveLabel,
					rangeThumbCount = 2,
					display = FILTER_DISPLAY.DEFAULT,
				} = cfg;

				// Filtre RANGE : on fournit uniquement le domaine min/max (pas d'options)
				if (selectionMode === FILTER_SELECTION_TYPES.RANGE) {
					const range = computeRangeDomain({ products, valueGetter });

					filters.push({
						field,
						title,
						selectionMode,
						options: [],
						range,
						isSearchable: false,
						search,
						formatRangeValue,
						formatRangeActiveLabel,
						rangeThumbCount,
					});
					continue;
				}

				const sortFn = typeof customSort === "function" ? customSort : defaultSort;

				// Options manuelles (si buildOptions fourni)
				const manualOptions =
					typeof buildOptions === "function" ? buildOptions(products) : null;

				// Options auto déduites des produits
				const autoOptions = buildAutoOptions({ products, valueGetter, labelGetter });

				// Counts calculés en excluant ce champ des filtres actifs
				const countsMap = getCountsForFilter(cfg);

				const formattedManual = formatAndEnrichOptions({
					options: manualOptions,
					formatMessage,
					formatLabel,
					buildOptionPrefix,
					countsMap,
					showCount,
				}).sort(sortFn);

				const formattedAuto = formatAndEnrichOptions({
					options: autoOptions,
					formatMessage,
					formatLabel,
					buildOptionPrefix,
					countsMap,
					showCount,
				}).sort(sortFn);

				// Fusion finale selon optionMode
				const options = mergeOptions({
					manualOptions: formattedManual,
					autoOptions: formattedAuto,
					optionMode,
				});

				// On évite d'afficher un filtre sans options (ou une seule option si interdit)
				if (options.length === 0 || (options.length === 1 && !allowSingleOption)) {
					continue;
				}

				// Recherche activée uniquement si demandé + assez d'options
				const isSearchable = allowSearch && options.length >= MIN_SEARCHABLE_OPTIONS;

				filters.push({
					field,
					title,
					selectionMode,
					options,
					range: null,
					isSearchable,
					search,
					display,
				});
			}

			if (filters.length === 0) {
				continue;
			}

			builtFacets.push({
				facetId,
				facetTitle,
				filters,
			});
		}

		return builtFacets;
	}, [
		facetConfigs,
		products,
		normalizedActiveFilters,
		formatMessage,
		facetsFromBackend,
		flatFilters,
		indexes,
		manualValueSetByField,
	]);

	return { filteredProducts, facets, activeFilters: normalizedActiveFilters };
};
